import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Alert, Box, IconButton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  getOrder,
  getProductsToCollect,
  updateOrderStatus,
} from '../../../api/Order';
import { Waiting } from '../../../components/Waiting/Waiting';
import { sharedStyles } from '../../../utils/Style';
import { CollectOrderTable } from './CollectOrderTable';
import { ScanBarcode } from '../../../components/Barcode/ScanBarcode';
import { OrderStatus } from '../../../interfaces/Order/OrderStatus';
import {
  getShipmentImages,
  uploadShipmentImage,
  deleteShipmentImage,
  deletePlatformShipmentImageAtIndex,
} from '../../../api/ShipmentImage';
import { ShipmentImage } from '../../../interfaces/ShipmentImage/ShipmentImage';
import { ShipmentImages } from './ShipmentImages';
import { playErrorBeep, playScanSuccess } from '../../../utils/Sound';
import {
  ProductToCollect,
  productToCollectFromOrderProduct,
} from '../../../interfaces/ProductToCollect/ProductToCollect';
import { PartnerPlatform } from '../../../interfaces/PartnerPlatform/PartnerPlatform';

export const CollectOrderPage = ({
  platform,
}: {
  platform?: PartnerPlatform;
}) => {
  const navigate = useNavigate();

  const { orderID: orderIDAsStr } = useParams<string>();

  const orderID = orderIDAsStr ? Number(orderIDAsStr) : -1;

  const [productsToCollect, setProductsToCollect] = useState<
    ProductToCollect[]
  >([]);
  const [shipmentImages, setShipmentImages] = useState<ShipmentImage[]>([]);
  const [scannedAmounts, setScannedAmounts] = useState<Record<string, number>>(
    {},
  );
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [loadingShipmentImages, setLoadingShipmentImages] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  const refreshOrder = () => {
    setLoadingOrder(true);

    if (platform) {
      getProductsToCollect(platform, orderID)
        .then(setProductsToCollect)
        .catch((err) => toast.error(err.message))
        .finally(() => setLoadingOrder(false));
    } else {
      getOrder(orderID)
        .then((o) => {
          setProductsToCollect(
            o.products.map(productToCollectFromOrderProduct),
          );
          setScannedAmounts({});
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoadingOrder(false));
    }
  };

  const refreshShipmentImages = () => {
    setLoadingShipmentImages(true);
    getShipmentImages(platform ?? 'internal', orderID)
      .then(setShipmentImages)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingShipmentImages(false));
  };

  useEffect(() => {
    refreshOrder();
    refreshShipmentImages();
  }, [orderID]);

  const goBack = () =>
    navigate(platform ? `/sales/${platform}` : `/sales/orders/${orderID}`);

  const onScan = (barcode: string) => {
    setShowScanner(false);

    const scannedProduct = productsToCollect.find((p) => p.barcode === barcode);
    if (!scannedProduct) {
      toast.error(`Sipariş içeriğinde ${barcode} barkodlu bir ürün bulunmuyor`);
      playErrorBeep();
      return;
    }

    const existingAmount = scannedAmounts[barcode] ?? 0;
    if (existingAmount >= scannedProduct.quantity) {
      toast.error(
        `${barcode} barkodlu üründen zaten ${existingAmount} adet eklenmiş`,
      );
      playErrorBeep();
      return;
    }

    playScanSuccess();
    setScannedAmounts({
      ...scannedAmounts,
      [barcode]: existingAmount + 1,
    });
  };

  const onUploadImage = (image: File) => {
    setUploadingImage(true);
    uploadShipmentImage(platform ?? 'internal', orderID, image)
      .then(() => {
        toast.success('Fotoğraf başarıyla eklendi');
        refreshShipmentImages();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setUploadingImage(false));
  };

  const onDeleteImage = (image: ShipmentImage, index: number) => {
    setUploadingImage(true);
    if (platform) {
      deletePlatformShipmentImageAtIndex(platform, orderID, index)
        .then(refreshShipmentImages)
        .catch((err) => toast.error(err.message))
        .finally(() => setUploadingImage(false));
    } else {
      deleteShipmentImage(image.id)
        .then(refreshShipmentImages)
        .catch((err) => toast.error(err.message))
        .finally(() => setUploadingImage(false));
    }
  };

  const handleConfirm = () => {
    if (platform) {
      navigate(`/sales/${platform}`);
      return;
    }

    setConfirming(true);
    updateOrderStatus([orderID], OrderStatus.READY_TO_SHIP)
      .then(() => {
        toast.success(
          "Sipariş başarıyla 'Kargolanmaya Hazır' olarak işaretlendi.",
        );
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setConfirming(false));
  };

  const fullFilledProducts =
    productsToCollect.filter(
      (p) => scannedAmounts[p.barcode ?? ''] === p.quantity,
    ) ?? [];

  const productsWithoutBarcode =
    productsToCollect.filter((p) => !p.barcode) ?? [];

  const isFullFilled = fullFilledProducts.length === productsToCollect.length;

  if (loadingOrder) {
    return <Waiting open />;
  }

  return (
    <Box sx={{ ...sharedStyles.pageBody, gap: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>Siparişi Hazırla</Typography>
      </Box>

      {productsWithoutBarcode.length > 0 && (
        <Alert severity='warning'>
          Aşağıdaki ürünler için barkod tanımlanmamış. Lütfen tüm ürünler için
          barkod tanımlandığından emin olunuz.
          {productsWithoutBarcode.map((p) => (
            <>
              <br />
              <Link
                to={`/products/catalog/${p.id}${
                  p.options.length ? '/options' : ''
                }`}
              >
                {p.name}{' '}
                {p.options.length
                  ? `(${p.options
                      .map((option) => option.optionValueName)
                      .join(', ')})`
                  : ''}
              </Link>
            </>
          ))}
        </Alert>
      )}

      {showScanner ? (
        <ScanBarcode onScan={onScan} onCancel={() => setShowScanner(false)} />
      ) : (
        <>
          <CollectOrderTable
            products={productsToCollect}
            shipmentImages={shipmentImages}
            scannedAmounts={scannedAmounts}
            fullFilledProducts={fullFilledProducts}
            confirming={confirming}
            uploading={uploadingImage}
            onClickScan={() => setShowScanner(true)}
            onUploadImage={onUploadImage}
            onConfirm={handleConfirm}
          />

          <ShipmentImages
            images={shipmentImages}
            waitingForBarcodeScan={!isFullFilled}
            onDelete={onDeleteImage}
            loading={loadingShipmentImages}
          />
        </>
      )}
    </Box>
  );
};
